import { Logger } from '@/Logger';

import { ApiError } from '../errors/ApiError';
import type {
  AnalysisRequest,
  AnalysisRequestWithTemplateOptions,
  AnalysisResultData,
  TextBlock,
} from '../TeoApi';
import { TeoCore } from '../TeoCore';
import type { BaseTextData, TextData, TextDataAnalysis, TextDataGenerator } from '../TeoStorage';
import { TeoEventTarget } from '../utility/TeoEventTarget';
import { AIHandler } from './AIHandler/AIHandler';
import { AnalysisResult } from './AnalysisResult';
import { DiffMarkers } from './DiffMarkers';
import { GeneratorUiHandler } from './generator/GeneratorUiHandler';
import type { HistoryEntry } from './history/HistoryEntry';
import { HistoryManager } from './history/HistoryManager';
import { MarkerManager } from './MarkerManager';
import { SpellCheckHandler } from './SpellCheckHandler';
import { StyleHandler } from './StyleHandler';
import { areSelectedOptionsEqual } from './TeoText.helpers';
import { TeoTextStore } from './TeoText.store';
import { TextUi, type TeoView } from './TextUi';
import { WordListHandler } from './WordListHandler/WordListHandler';

const LOG = Logger('TeoText');

export type TextEvents = {
  configChanged: (detail: Partial<AnalysisConfiguration> | null) => void;
  textChanged: (detail: string) => void;
  textChangedExternal: (options?: { preserveDomSelection?: boolean }) => void;
  gotAnalysis: (detail: AnalysisResult) => void;
  requestedAnalysis: (detail: AnalysisRequestWithTemplateOptions) => void;
  analysisReadinessChanged: (detail: boolean) => void;
  analysisStateChanged: (options?: { trigger?: AnalysisRequest['trigger'] }) => void;
  editable(detail: boolean): void;
  analysisPending: (detail: boolean) => void;
  historyChanged: (detail: HistoryEntry[]) => void;
};

export type AnalysisConfiguration = Record<
  'audience' | 'strategy' | 'medium' | 'is_informal',
  number
>;

export type SelectedOptions = Record<string, string | undefined>;

export type AnalysisState = {
  isCurrent: boolean;
  canAnalyze: boolean;
  analysisPending: boolean;
  brandFitUpToDate: boolean;
  dictionaryUpToDate: boolean;
};

export type TeoTextProps = Omit<Partial<BaseTextData>, 'id'> & {
  id: number;
} & Partial<TextDataAnalysis> &
  Partial<TextDataGenerator>;

export interface TeoTextCreateOptions {
  shouldStoreAfterCreate?: boolean;
  isTemporary?: boolean;
}

export class TeoText {
  // #state: ReactiveState<TextState>;
  ui: TextUi;
  #store: TeoTextStore;
  generatorUi: GeneratorUiHandler;
  id: number;
  text: string = '';
  serialized: string | undefined;
  analysis: AnalysisResult | null = null;
  events = new TeoEventTarget<TextEvents>();
  core: TeoCore;
  getTextArray: (() => TextBlock[]) | null = null;
  ai: AIHandler;
  wordLists: WordListHandler;
  spellCheck: SpellCheckHandler;
  style: StyleHandler;
  markers: MarkerManager;
  diff: DiffMarkers;
  analysisPending = false;
  isTemporary = false;
  private _isEditable = true;
  history: HistoryManager;
  private _analysisConfiguration?: Partial<AnalysisConfiguration>;
  private _readyToAnalyze = false;
  private listeners: Array<() => void> = [];
  private _disposed = false;
  lastEdited: number;
  textSessionId: string | null = null;

  clone(): TeoText {
    return new TeoText(
      this.core,
      { ...this.getTextData(), textSessionId: null },
      { isTemporary: this.isTemporary },
    );
  }

  constructor(core: TeoCore, data: TeoTextProps, options: TeoTextCreateOptions = {}) {
    const { shouldStoreAfterCreate = true, isTemporary = false } = options;

    this.core = core;

    this.id = data.id;
    this.text = data.text ?? '';
    this.lastEdited = data.lastEdited ?? Date.now();
    this.serialized = data.serialized;
    this.textSessionId = data.textSessionId ?? null;
    this._analysisConfiguration = data.analysisConfiguration;
    this.ui = new TextUi({
      title: `Text ${this.id + 1}`,
      ...data.uiState,
    });

    this.#store = new TeoTextStore(
      this,
      data.storeState ?? {
        selectedPromptOptions: {},
        view: core.dynamicConfig.options.textGeneratorEnabled ? 'new' : 'performance',
        currentTemplateId: undefined,
        previousView: null,
        performanceMode: 'manual',
      },
    );

    this.isTemporary = isTemporary;
    this.history = new HistoryManager(this);
    this.markers = new MarkerManager(this);
    this.ai = new AIHandler(this);
    this.wordLists = new WordListHandler(this);
    this.spellCheck = new SpellCheckHandler(this);
    this.style = new StyleHandler(this);
    this.diff = new DiffMarkers(this);

    this.generatorUi = new GeneratorUiHandler(this, data.generatorUi);

    if (data.analysis) {
      this.analysis = AnalysisResult.fromJSON(this, data.analysis);
      // ???: do we need to trigger an event here?
    }
    if ('history' in data && data.history) {
      this.history.restoreFromJSON(data.history);
      // ???: do we need to trigger an event here?
    }

    this.events.addEventListener('analysisPending', (pending) => {
      this.analysisPending = pending;
      this.events.triggerEvent('analysisStateChanged', void 0);
    });
    this.store.addEventListener('loadingCount', () => {
      // while history is active, the text is not editable
      if (this.history.viewIsActive) return;
      this.isEditable = !this.store.state.isLoading;
    });
    this.ui.events.addEventListener('textProperty', () => this.markers.syncPerformanceMarkers());
    this.store.addEventListener('view', (viewName: TeoView) => {
      if (viewName === 'performance') this.markers.syncPerformanceMarkers();
      // if (viewName === 'brand-scanner') this.syncAnalysis();
    });
    this.ui.events.addEventListener('changed', () => {
      this.save();
    });
    this.store.addEventListener('*', () => {
      this.save();
    });
    let lastTemplateId = this.state.currentTemplateId;
    let lastSelectedOptions = this.state.selectedPromptOptions;
    this.store.addEventListener('selectedPromptOptions', (selectedPromptOptions) => {
      if (!areSelectedOptionsEqual(lastSelectedOptions, selectedPromptOptions)) {
        this.textSessionId = null;
      }
      lastSelectedOptions = selectedPromptOptions;
      this.touch();
    });
    this.store.addEventListener('currentTemplateId', (currentTemplateId) => {
      if (currentTemplateId !== lastTemplateId) {
        this.textSessionId = null;
      }
      lastTemplateId = currentTemplateId;
      this.touch();
    });

    if (shouldStoreAfterCreate) {
      this.save();
    }
    // this.listeners.push(this.core.dynamicConfig.addEventListener('textConfigOptions', () => {}));
  }

  get store(): TeoTextStore {
    return this.#store;
  }

  get state() {
    return this.store.state;
  }

  set analysisConfiguration(analysisConfig: Partial<AnalysisConfiguration> | undefined) {
    if (this._analysisConfiguration === analysisConfig) return;
    if (
      this._analysisConfiguration &&
      this._analysisConfiguration.audience === analysisConfig?.audience &&
      this._analysisConfiguration.strategy === analysisConfig?.strategy &&
      this._analysisConfiguration.medium === analysisConfig?.medium &&
      this._analysisConfiguration.is_informal === analysisConfig?.is_informal
    ) {
      return;
    }
    this._analysisConfiguration = analysisConfig;
    this.analysisConfigChanged();
    this.events.triggerEvent('configChanged', analysisConfig as AnalysisConfiguration);
    this.checkReadyToAnalyze();
    this.save();
  }

  get analysisConfiguration(): Partial<AnalysisConfiguration> | undefined {
    return this._analysisConfiguration;
  }

  set isEditable(editable: boolean) {
    if (editable === this._isEditable) return;
    this._isEditable = editable;
    this.events.triggerEvent('editable', editable);
  }

  get isEditable(): boolean {
    return this._isEditable;
  }

  get whiteListActive(): boolean {
    return this.wordLists.whiteList.isActive;
  }

  get disposed(): boolean {
    return this._disposed;
  }

  checkReadyToAnalyze() {
    const ready = this.state.allPromptOptionsSelected && this.textIsSet();
    if (ready !== this._readyToAnalyze) {
      this._readyToAnalyze = ready;
      this.events.triggerEvent('analysisReadinessChanged', this._readyToAnalyze);
      this.events.triggerEvent('analysisStateChanged', void 0);
    }
    return ready;
  }

  getBaseTextData(): BaseTextData {
    return {
      id: this.id,
      text: this.text,
      lastEdited: this.lastEdited,
      serialized: this.serialized,
      analysisConfiguration: { ...this.analysisConfiguration },
      uiState: {
        ...this.ui.state,
      },
      storeState: this.store.toJSON(),
      // analysis: this.analysisState.isCurrent ? this.analysis?.toJSON() : undefined, // TODO: find a way to maybe restore analysis independent of the text?
      analysis: this.analysis?.toJSON(),
      textSessionId: this.textSessionId,
    };
  }

  getTextData(): TextData {
    return structuredClone({
      ...this.getBaseTextData(),

      history: this.history?.toJSON(),
      generatorUi: this.generatorUi?.toJSON(),
    });
  }

  textIsSet(): boolean {
    return this.text.length > 0;
  }

  async analyze(options: { trigger?: AnalysisRequest['trigger'] }) {
    LOG.dev('@.analyze', this, this.store.state.view);
    this.events.triggerEvent('analysisPending', true);
    await this.store.withLoading(
      () =>
        Promise.all([
          this.sendAnalysisRequest(options),
          (
            this.store.state.view == 'brand-scanner' ||
            this.core.dynamicConfig.options.alwaysCheckBrandFit
          ) ?
            this.wordLists.brandFit.scan()
          : undefined,
          this.store.state.view == 'dictionary' ? this.wordLists.dictionary.scan() : undefined,
          this.store.state.view == 'spellcheck' ? this.spellCheck?.check() : undefined,
        ]),
      'TPI Analyse...',
    );
    this.events.triggerEvent('analysisPending', false);
    this.events.triggerEvent('analysisStateChanged', { trigger: options.trigger });
  }

  async sendAnalysisRequest({ trigger }: { trigger?: AnalysisRequest['trigger'] }) {
    const template_id = this.state.currentTemplate?.id;
    const selected_options = this.state.selectedPromptOptions;
    if (
      !this.store.state.allPromptOptionsSelected ||
      !template_id ||
      Object.keys(selected_options).length === 0
    ) {
      console.error('No configuration set for analysis');
      return;
    }
    let text: string | TextBlock[] = this.text;
    if (this.getTextArray !== null) {
      text = this.getTextArray();
    }
    const testRequest: AnalysisRequestWithTemplateOptions = {
      text,
      evaluation_profile_id: this.core.dynamicConfig.options.evaluationProfileId,
      selected_options,
      template_id,
      use_whitelist: this.whiteListActive,
      trigger: trigger === 'auto_suggestion' ? undefined : trigger,
      ...(this.textSessionId ? { text_session_id: this.textSessionId } : {}),
    };
    const lastInstruction = this.analysis?.currentInstruction;
    if (lastInstruction) {
      testRequest.last_instruction_id = lastInstruction.id;
      testRequest.last_text_property = lastInstruction.text_property_name;
    }
    if (this.core.dynamicConfig.options.useExpParser) {
      testRequest.use_exp_parser = true;
    }
    this.events.triggerEvent('requestedAnalysis', testRequest);

    const result = await this.core.api.getAnalysisWithTemplate(testRequest);

    if (ApiError.isError(result)) {
      this.core.notifications.add({
        title: 'Analyse fehlgeschlagen',
        message: `${result.error}`,
        mode: 'error',
      });
      console.error('Error in analysis result:', result);
      return;
    }

    const { analysisConfig, ...analysisResult } = result;

    this.updateResult(analysisResult, text);
    this.textSessionId = analysisResult.text_session_id ?? null;
    if (trigger !== 'auto_suggestion') {
      this.history.addHistoryEntry();
    }
    this.analysisConfiguration = analysisConfig;
    this.events.triggerEvent('configChanged', analysisConfig);

    if (result.usage_quota?.type === 'ANALYSIS') {
      this.core.updateUsageQuota(result.usage_quota);
    }
  }

  updateResult(result: AnalysisResultData, text: string | TextBlock[]) {
    this.analysis = new AnalysisResult(result, this, text);
    this.events.triggerEvent('analysisStateChanged', void 0);
    this.events.triggerEvent('gotAnalysis', this.analysis);
  }

  get analysisState(): AnalysisState {
    return {
      isCurrent: !!this.analysis?.createdAt && this.analysis.createdAt >= this.lastEdited,
      canAnalyze: this.textIsSet(), // TODO: currently textIsSet (here) + allPromptOptionsSelected needs to be checked in the UI. This should be done in the store.
      analysisPending: this.analysisPending,
      brandFitUpToDate: this.wordLists.brandFit.isUpToDate,
      dictionaryUpToDate: this.wordLists.dictionary.isUpToDate,
    };
  }

  updateText(
    newText: string | undefined,
    serialized: string | undefined = undefined,
    isExternal: boolean = true,
  ) {
    if (newText === this.text) {
      // The editor reports its state for a text it was handed, e.g. after a full rewrite.
      if (serialized !== undefined && serialized !== this.serialized) {
        this.serialized = serialized;
        this.save();
      }
      return;
    }
    if (!this._isEditable) {
      console.error('Text changed while not editable');
      return;
    }
    // if (serialized !== undefined && serialized !== this.serialized) {
    this.serialized = serialized;
    // }
    if (newText !== undefined && newText !== this.text) {
      this.text = newText;
      if (isExternal) {
        this.events.triggerEvent('textChangedExternal', undefined);
      } else {
        this.events.triggerEvent('textChanged', this.text);
      }
    }
    this.lastEdited = Date.now();
    this.save();
    this.events.triggerEvent('analysisStateChanged', void 0);
    // this.events.triggerEvent('textChanged', this.text);
    this.checkReadyToAnalyze();
  }

  updateTitle(title: string) {
    this.ui.update('title', title);
    this.save();
  }

  updateTextArrayFunction(getTextArray: () => TextBlock[]) {
    this.getTextArray = getTextArray;
  }

  #toStoreDebounceTimeout: ReturnType<typeof setTimeout> | undefined;

  private saveDebounced() {
    if (this.#toStoreDebounceTimeout) {
      clearTimeout(this.#toStoreDebounceTimeout);
    }
    this.#toStoreDebounceTimeout = setTimeout(() => {
      const data = this.getTextData();
      LOG.dev('@.saveDebounced', data);
      this.core.storage.saveText(data);
    }, 100);
  }

  save() {
    if (this.isTemporary || this._disposed) return; // Do not store temporary or disposed texts
    // small debounce
    this.saveDebounced();
  }

  touch(): void {
    this.lastEdited = Date.now();
    this.events.triggerEvent('analysisStateChanged', void 0);
  }

  // TODO make this private again and handle whitelist toggle more on the configuration side
  public analysisConfigChanged() {
    if (this.analysisState.isCurrent) {
      // TODO: implement something real here
      // it depends on if analysis should be stored too, or, or, or...
      this.lastEdited = Date.now();
      this.events.triggerEvent('analysisStateChanged', void 0);
    }
  }

  /**
   * Update the text data.
   * @param data The text data to update.
   * @param opts The options to update the text data.
   * @param opts.preserveDomSelection Generator history updated den angezeigten Text, dieser
   *                                  reisst normalerweise die Selection zu sich, deshalb nun mit
   *                                  neuem Parameter "preserveDomSelection".
   */
  updateTextData(data: BaseTextData, opts?: { preserveDomSelection?: boolean }) {
    LOG.dev('@.updateTextData', data);
    // this.id = data.id;
    this.text = data.text ?? '';
    this.lastEdited = data.lastEdited ?? Date.now();
    this.serialized = data.serialized;
    this._analysisConfiguration = data.analysisConfiguration;
    this.ui.updateState({
      title: `Text ${this.id + 1}`,
      ...data.uiState,
    });
    this.#store.updateFromStoredState(
      data.storeState ?? {
        selectedPromptOptions: {},
        view: this.core.dynamicConfig.options.textGeneratorEnabled ? 'new' : 'performance',
        previousView: null,
        currentTemplateId: undefined,
        performanceMode: 'manual',
      },
    );
    this.textSessionId = data.textSessionId ?? null;
    if (data.analysis) {
      this.updateResult(data.analysis.data, this.text);
      // this.analysis = AnalysisResult.fromJSON(this, data.analysis);

      if (this.analysis) this.events.triggerEvent('gotAnalysis', this.analysis);
      this.events.triggerEvent('analysisStateChanged', void 0);
    }

    this.events.triggerEvent('textChangedExternal', {
      preserveDomSelection: opts?.preserveDomSelection,
    });
    this.events.triggerEvent('textChanged', this.text);
  }

  dispose() {
    this.listeners.forEach((listener) => listener());
    this.listeners = [];
    this.#store.dispose();
    this._disposed = true;
  }
}
